import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Badge, ToastContainer } from './ui';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import BulkEnroll from './BulkEnroll';
import ClassLevelFields from './ClassLevelFields';

const STEPS = [
  { key: 'teachers', label: 'Teachers' },
  { key: 'classes', label: 'Classes' },
  { key: 'students', label: 'Students' },
  { key: 'allocations', label: 'Subject Allocations' },
  { key: 'timetable', label: 'Timetable' },
  { key: 'review', label: 'Review & Complete' },
];

const EMPTY_TEACHER = { staff_id: '', full_name: '', national_id: '', phone: '', email: '' };
const EMPTY_CLASS = { name: '', stream: '', homeroom_teacher_id: '' };

const EMPTY_ALLOCATION = { subject_id: '', teacher_id: '', class_id: '', term_id: '' };
const EMPTY_TIMETABLE = { class_id: '', subject_allocation_id: '', day_of_week: 'Monday', start_time: '08:00', end_time: '09:00', room: '' };
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

function AcademicSetupWizard({ token }) {
  const [activeStep, setActiveStep] = useState('teachers');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [terms, setTerms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [timetableSlots, setTimetableSlots] = useState([]);
  const [classAllocations, setClassAllocations] = useState([]);

  const [teacherForm, setTeacherForm] = useState(EMPTY_TEACHER);
  const [classForm, setClassForm] = useState(EMPTY_CLASS);
  const [allocationForm, setAllocationForm] = useState(EMPTY_ALLOCATION);
  const [timetableForm, setTimetableForm] = useState(EMPTY_TIMETABLE);
  const [saving, setSaving] = useState(false);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, teachersRes, classesRes, subjectsRes, termsRes, allocationsRes, timetableRes] = await Promise.all([
        fetch('/api/v1/academic-setup/status', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/teachers', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/subjects', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/terms', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/subject-allocations', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/timetable', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStatus(await statusRes.json());
      setTeachers(await teachersRes.json());
      setClasses(await classesRes.json());
      setSubjects(await subjectsRes.json());
      setTerms(await termsRes.json());
      setAllocations(await allocationsRes.json());
      setTimetableSlots(await timetableRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshStatus = async () => {
    try {
      const response = await fetch('/api/v1/academic-setup/status', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) setStatus(await response.json());
    } catch {
      // status refresh isn't critical to the action that just succeeded
    }
  };

  const isDone = (key) => {
    if (!status) return false;
    const c = status.counts;
    switch (key) {
      case 'teachers': return c.teachers > 0;
      case 'classes': return c.classes > 0;
      case 'students': return c.students_enrolled > 0;
      case 'allocations': return c.allocations > 0;
      case 'timetable': return c.timetable_slots > 0;
      default: return false;
    }
  };

  const goNext = () => {
    const idx = STEPS.findIndex(s => s.key === activeStep);
    if (idx < STEPS.length - 1) setActiveStep(STEPS[idx + 1].key);
  };
  const goBack = () => {
    const idx = STEPS.findIndex(s => s.key === activeStep);
    if (idx > 0) setActiveStep(STEPS[idx - 1].key);
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch('/api/v1/teachers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(teacherForm),
      });
      if (response.ok) {
        setTeacherForm(EMPTY_TEACHER);
        addToast('Teacher added');
        const teachersRes = await fetch('/api/v1/teachers', { headers: { Authorization: `Bearer ${token}` } });
        setTeachers(await teachersRes.json());
        refreshStatus();
      } else {
        addToast(await safeErrorMessage(response, 'Failed to add teacher'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddClass = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch('/api/v1/classes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...classForm, stream: classForm.stream.trim() || null, homeroom_teacher_id: classForm.homeroom_teacher_id || null }),
      });
      if (response.ok) {
        setClassForm(EMPTY_CLASS);
        addToast('Class added');
        const classesRes = await fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } });
        setClasses(await classesRes.json());
        refreshStatus();
      } else {
        addToast(await safeErrorMessage(response, 'Failed to add class'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAllocation = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch('/api/v1/subject-allocations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(allocationForm),
      });
      if (response.ok) {
        setAllocationForm(EMPTY_ALLOCATION);
        addToast('Subject allocation added');
        const allocationsRes = await fetch('/api/v1/subject-allocations', { headers: { Authorization: `Bearer ${token}` } });
        setAllocations(await allocationsRes.json());
        refreshStatus();
      } else {
        addToast(await safeErrorMessage(response, 'Failed to add allocation'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTimetableClassChange = async (classId) => {
    setTimetableForm(prev => ({ ...prev, class_id: classId, subject_allocation_id: '' }));
    if (!classId) { setClassAllocations([]); return; }
    try {
      const response = await fetch(`/api/v1/subject-allocations?class_id=${classId}`, { headers: { Authorization: `Bearer ${token}` } });
      setClassAllocations(response.ok ? await response.json() : []);
    } catch {
      setClassAllocations([]);
    }
  };

  const handleAddTimetable = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch('/api/v1/timetable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          subject_allocation_id: timetableForm.subject_allocation_id,
          day_of_week: timetableForm.day_of_week,
          start_time: timetableForm.start_time,
          end_time: timetableForm.end_time,
          room: timetableForm.room,
        }),
      });
      if (response.ok) {
        setTimetableForm(EMPTY_TIMETABLE);
        setClassAllocations([]);
        addToast('Timetable slot added');
        const timetableRes = await fetch('/api/v1/timetable', { headers: { Authorization: `Bearer ${token}` } });
        setTimetableSlots(await timetableRes.json());
        refreshStatus();
      } else if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        addToast(data.error || 'This slot clashes with an existing timetable entry', 'error');
      } else {
        addToast(await safeErrorMessage(response, 'Failed to add timetable slot'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFinishSetup = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/v1/academic-setup/complete', {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setStatus(await response.json());
        addToast('Academic setup marked complete');
      } else {
        addToast(await safeErrorMessage(response, 'Failed to complete setup'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card>Loading setup status…</Card>;

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Academic Setup</h2>
        {status?.completed && (
          <div className="alert alert-success" style={{ marginTop: '0.5rem' }}>
            ✓ Academic setup complete. You can still revisit any step below to make changes.
          </div>
        )}
      </div>

      <Card>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {STEPS.map((step, i) => {
            const done = step.key !== 'review' && isDone(step.key);
            const active = step.key === activeStep;
            return (
              <Button
                key={step.key}
                variant={active ? 'primary' : done ? 'success' : 'secondary'}
                size="sm"
                onClick={() => setActiveStep(step.key)}
              >
                {done && <CheckCircleIcon style={{ width: 14, height: 14 }} />}
                {i + 1}. {step.label}
              </Button>
            );
          })}
        </div>
      </Card>

      {activeStep === 'teachers' && (
        <Card title="Step 1 — Teachers">
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Add the teaching staff who will be assigned to classes and subjects.</p>
          <form onSubmit={handleAddTeacher}>
            <div className="form-row">
              <div className="form-group">
                <label>Staff ID *</label>
                <input type="text" value={teacherForm.staff_id} onChange={(e) => setTeacherForm({ ...teacherForm, staff_id: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" value={teacherForm.full_name} onChange={(e) => setTeacherForm({ ...teacherForm, full_name: e.target.value })} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>National ID</label>
                <input type="text" value={teacherForm.national_id} onChange={(e) => setTeacherForm({ ...teacherForm, national_id: e.target.value })} placeholder="00-000000A00" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" value={teacherForm.phone} onChange={(e) => setTeacherForm({ ...teacherForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={teacherForm.email} onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })} />
            </div>
            <Button type="submit" variant="primary" disabled={saving}>Add Teacher</Button>
          </form>

          <div style={{ marginTop: '1.5rem' }}>
            <strong style={{ fontSize: '0.85rem' }}>Existing teachers ({teachers.length})</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              {teachers.map(t => <Badge key={t.id} variant="default">{t.full_name}</Badge>)}
            </div>
          </div>
          <p style={{ marginTop: '1rem' }}><Link to="/teachers">Manage all teachers →</Link></p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={goNext}>Next →</Button>
          </div>
        </Card>
      )}

      {activeStep === 'classes' && (
        <Card title="Step 2 — Classes">
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Set up the cohorts/streams students will be enrolled into.</p>
          <form onSubmit={handleAddClass}>
            <ClassLevelFields token={token} name={classForm.name} stream={classForm.stream} onChange={(changes) => setClassForm({ ...classForm, ...changes })} />
            <div className="form-row">
              <div className="form-group">
                <label>Homeroom Teacher</label>
                <select value={classForm.homeroom_teacher_id} onChange={(e) => setClassForm({ ...classForm, homeroom_teacher_id: e.target.value })}>
                  <option value="">None</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </div>
            </div>
            <Button type="submit" variant="primary" disabled={saving}>Add Class</Button>
          </form>

          <div style={{ marginTop: '1.5rem' }}>
            <strong style={{ fontSize: '0.85rem' }}>Existing classes ({classes.length})</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              {classes.map(c => <Badge key={c.id} variant="default">{c.display_name}</Badge>)}
            </div>
          </div>
          <p style={{ marginTop: '1rem' }}><Link to="/classes">Manage all classes →</Link></p>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={goBack}>← Back</Button>
            <Button variant="secondary" onClick={goNext}>Next →</Button>
          </div>
        </Card>
      )}

      {activeStep === 'students' && (
        <Card title="Step 3 — Students">
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
            Enroll students into a class. <Link to="/students/new">+ Add a new student</Link> first if they don't exist yet.
          </p>
          <BulkEnroll token={token} embedded />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={goBack}>← Back</Button>
            <Button variant="secondary" onClick={() => { fetchAll(); goNext(); }}>Next →</Button>
          </div>
        </Card>
      )}

      {activeStep === 'allocations' && (
        <Card title="Step 4 — Subject Allocations">
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Assign a teacher to teach a subject to a class for a term.</p>
          <form onSubmit={handleAddAllocation}>
            <div className="form-row">
              <div className="form-group">
                <label>Subject *</label>
                <select value={allocationForm.subject_id} onChange={(e) => setAllocationForm({ ...allocationForm, subject_id: e.target.value })} required>
                  <option value="">Select Subject</option>
                  {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Teacher *</label>
                <select value={allocationForm.teacher_id} onChange={(e) => setAllocationForm({ ...allocationForm, teacher_id: e.target.value })} required>
                  <option value="">Select Teacher</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Class *</label>
                <select value={allocationForm.class_id} onChange={(e) => setAllocationForm({ ...allocationForm, class_id: e.target.value })} required>
                  <option value="">Select Class</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Term *</label>
                <select value={allocationForm.term_id} onChange={(e) => setAllocationForm({ ...allocationForm, term_id: e.target.value })} required>
                  <option value="">Select Term</option>
                  {terms.map(t => <option key={t.id} value={t.id}>Term {t.term_number} - {t.year}</option>)}
                </select>
              </div>
            </div>
            <Button type="submit" variant="primary" disabled={saving}>Add Allocation</Button>
          </form>

          <div style={{ marginTop: '1.5rem' }}>
            <strong style={{ fontSize: '0.85rem' }}>Existing allocations ({allocations.length})</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
              {allocations.map(a => <Badge key={a.id} variant="default">{a.subject_name} · {a.teacher_name} · {a.class_display_name}</Badge>)}
            </div>
          </div>
          <p style={{ marginTop: '1rem' }}><Link to="/subject-allocations">Manage all allocations →</Link></p>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={goBack}>← Back</Button>
            <Button variant="secondary" onClick={goNext}>Next →</Button>
          </div>
        </Card>
      )}

      {activeStep === 'timetable' && (
        <Card title="Step 5 — Timetable">
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Only needed if this school uses subject-period attendance — schedule when each allocation is taught.</p>
          <form onSubmit={handleAddTimetable}>
            <div className="form-row">
              <div className="form-group">
                <label>Class *</label>
                <select value={timetableForm.class_id} onChange={(e) => handleTimetableClassChange(e.target.value)} required>
                  <option value="">Select Class</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Subject Allocation *</label>
                <select value={timetableForm.subject_allocation_id} onChange={(e) => setTimetableForm({ ...timetableForm, subject_allocation_id: e.target.value })} required disabled={!timetableForm.class_id}>
                  <option value="">{timetableForm.class_id ? 'Select Subject / Teacher' : 'Select a class first'}</option>
                  {classAllocations.map(a => <option key={a.id} value={a.id}>{a.subject_name} — {a.teacher_name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Day *</label>
                <select value={timetableForm.day_of_week} onChange={(e) => setTimetableForm({ ...timetableForm, day_of_week: e.target.value })} required>
                  {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Room</label>
                <input type="text" value={timetableForm.room} onChange={(e) => setTimetableForm({ ...timetableForm, room: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Time *</label>
                <input type="time" value={timetableForm.start_time} onChange={(e) => setTimetableForm({ ...timetableForm, start_time: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>End Time *</label>
                <input type="time" value={timetableForm.end_time} onChange={(e) => setTimetableForm({ ...timetableForm, end_time: e.target.value })} required />
              </div>
            </div>
            <Button type="submit" variant="primary" disabled={saving}>Add Timetable Slot</Button>
          </form>

          <div style={{ marginTop: '1.5rem' }}>
            <strong style={{ fontSize: '0.85rem' }}>Existing slots ({timetableSlots.length})</strong>
          </div>
          <p style={{ marginTop: '1rem' }}><Link to="/timetable">Manage full timetable →</Link></p>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={goBack}>← Back</Button>
            <Button variant="secondary" onClick={goNext}>Next →</Button>
          </div>
        </Card>
      )}

      {activeStep === 'review' && status && (
        <Card title="Step 6 — Review & Complete">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              ['teachers', 'Teachers', status.counts.teachers],
              ['classes', 'Classes', status.counts.classes],
              ['students', 'Students enrolled', `${status.counts.students_enrolled} / ${status.counts.students_total}`],
              ['allocations', 'Subject Allocations', status.counts.allocations],
              ['timetable', 'Timetable Slots', status.counts.timetable_slots],
            ].map(([key, label, value]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{value}</span>
                  {isDone(key)
                    ? <Badge variant="success">Ready</Badge>
                    : <Badge variant="warning">Not started</Badge>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            {status.completed ? (
              <Badge variant="success">✓ Academic setup complete</Badge>
            ) : (
              <Button variant="primary" onClick={handleFinishSetup} disabled={saving}>Finish Setup</Button>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '1rem' }}>
            <Button variant="secondary" onClick={goBack}>← Back</Button>
          </div>
        </Card>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default AcademicSetupWizard;
